import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Check, X, Briefcase, Clock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FraternityInbox = ({ user }) => {
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/fraternity/invitations/me`,
        { headers: { Authorization: `Bearer ${token}` } });
      setIncoming(res.data.incoming || []);
      setOutgoing(res.data.outgoing || []);
    } catch (e) {
      // ignored
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const respond = async (id, accept) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/fraternity/invitations/${id}/respond`, { accept },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(accept ? 'Accepted — added to your projects' : 'Declined');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not respond');
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: '#080414', fontFamily: "'Poppins', system-ui, sans-serif" }} data-testid="fraternity-inbox-page">
      <header data-web-chrome="nav" className="border-b border-white/10 bg-black/40 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/fraternity')} className="text-sm text-white/70 hover:text-white flex items-center gap-1.5" data-testid="back-to-fraternity">
            <ArrowLeft size={14} /> Back
          </button>
          <h1 className="text-base font-bold">Crew invitations</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <section>
          <h2 className="text-xs uppercase tracking-wider text-white/70 font-bold mb-3" data-testid="incoming-header">Incoming invitations · {incoming.length}</h2>
          {loading ? <p className="text-white/70 text-sm">Loading…</p>
            : incoming.length === 0 ? <p className="text-white/70 italic text-sm">No incoming invitations.</p>
            : (
              <div className="space-y-3" data-testid="incoming-list">
                {incoming.map((inv) => (
                  <div key={inv.id} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start gap-3" data-testid={`inv-row-${inv.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#f472b6] to-[#c084fc] flex items-center justify-center flex-shrink-0">
                      <Briefcase size={18} weight="bold" className="text-black" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold leading-tight truncate" data-testid={`inv-title-${inv.id}`}>{inv.movie_title}</div>
                      <div className="text-[11px] text-[#facc15] uppercase tracking-wider font-bold mt-0.5">{inv.role}</div>
                      {inv.note && <p className="text-xs text-white/60 mt-1.5 leading-relaxed">{inv.note}</p>}
                      <div className="flex items-center gap-2 text-[10px] text-white/70 mt-2">
                        <Clock size={10} /> {new Date(inv.created_at).toLocaleDateString()}
                        <span className="ml-1 uppercase tracking-wider font-bold">· {inv.status}</span>
                      </div>
                    </div>
                    {inv.status === 'pending' && (
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <button data-testid={`accept-${inv.id}`} onClick={() => respond(inv.id, true)} className="bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-300 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1">
                          <Check size={12} weight="bold" /> Accept
                        </button>
                        <button data-testid={`decline-${inv.id}`} onClick={() => respond(inv.id, false)} className="bg-rose-500/15 hover:bg-rose-500/25 border border-rose-500/30 text-rose-300 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1">
                          <X size={12} weight="bold" /> Decline
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
        </section>

        <section>
          <h2 className="text-xs uppercase tracking-wider text-white/70 font-bold mb-3">My applications · {outgoing.length}</h2>
          {outgoing.length === 0 ? <p className="text-white/70 italic text-sm">No outgoing applications.</p>
            : (
              <div className="space-y-3" data-testid="outgoing-list">
                {outgoing.map((inv) => (
                  <div key={inv.id} className="bg-white/[0.03] border border-white/8 rounded-xl p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Briefcase size={18} className="text-white/60" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold leading-tight truncate">{inv.movie_title}</div>
                      <div className="text-[11px] text-[#facc15] uppercase tracking-wider font-bold mt-0.5">{inv.role}</div>
                      <div className="flex items-center gap-2 text-[10px] text-white/70 mt-2">
                        <Clock size={10} /> {new Date(inv.created_at).toLocaleDateString()}
                        <span className="ml-1 uppercase tracking-wider font-bold">· {inv.status}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </section>
      </div>
    </div>
  );
};

export default FraternityInbox;
